import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { Button } from "@shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { SelectCity } from "@/features/settings/citys/SelectCity";
import { SelectQuartier } from "@/features/settings/citys/SelectQuartier";
import { MultiSelectProduct } from "@features/products/components/MultiSelectProduct";
import { useAuth } from "@features/auth/contexts/AuthContext";
import PricingBar from "./PricingBar";
import { useEffect, useState } from "react";
import { usePricingQuery } from "@/features/settings/useSettings";
import { ShoppingBag, Warehouse } from "lucide-react";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Switch } from "@shared/components/ui/switch";
import { SelectUser } from "@/features/users/SelectUser";

export function SelectCard({ title, description, icon, isSelected, onSelect }) {
  const { t } = useTranslation();
  return (
    <Card
      className={`w-full cursor-pointer transition-all ${isSelected
        ? "border-primary shadow-sm scale-105"
        : "hover:border-primary/50 hover:shadow-sm"
        }`}
      onClick={onSelect}
    >
      <CardHeader className="py-2">
        <div className="flex items-center gap-4">
          <div
            className={`p-2 rounded-full ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
              }`}
          >
            {icon}
          </div>
          <div>
            <CardTitle className="text-lg">{t(title)}</CardTitle>
          </div>
          <CardDescription className="text-sm">{description}</CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
}

const PackageForm = ({ initialData, action, onSubmit, actionIsLoading }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientObject, setSelectedClientObject] = useState(null);

  const [city, setCity] = useState(null);

  useEffect(() => {
    if (initialData) {
      setCity(initialData.customer.city);
    }
  }, [initialData]);

  const { data, isLoading } = usePricingQuery({
    city: city,
    userId: auth.user._id,
  });

  const productSchema = z.object({
    id: z.string(),
    name: z.string(),
    quantity: z.number().int().min(1),
    price: z.number().min(0),
    allowOpening: z.boolean().default(true),
    allowFitting: z.boolean().default(true),
    allowExchange: z.boolean().default(true),
  });

  const costsSchema = z.object({
    deliveryFees: z.number().min(0),
    refusalFees: z.number().min(0),
    cancellationFees: z.number().min(0),
  });

  const customerSchema = z.object({
    name: z.string().min(
      1,
      t("packages.form.validation.required", {
        field: t("packages.form.customerName"),
      })
    ),
    city: z.string().min(
      1,
      t("packages.form.validation.required", {
        field: t("packages.form.customerCity"),
      })
    ),
    secteur: z.string().optional(),
    phone: z
      .string()
      .min(10, t("packages.form.validation.phoneLength"))
      .max(10, t("packages.form.validation.phoneLength"))
      .regex(/^0/, t("packages.form.validation.phoneStartsWith0")),
    address: z.string().min(
      1,
      t("packages.form.validation.required", {
        field: t("packages.form.customerAddress"),
      })
    ),
  });

  const formSchema = z.object({
    customer: customerSchema,
    totalAmount: z.number().min(0),
    fromStock: z.boolean().default(false),
    note: z.string().optional(),
    totalCost: z.number().min(0),
    costs: costsSchema,
    products: z
      .array(productSchema)
      .min(1, t("packages.form.validation.minProducts")),
  });

  const initialFormValues = initialData || {
    customer: {
      name: "",
      city: "",
      secteur: "",
      phone: "",
      address: "",
    },
    fromStock: false,
    totalAmount: 0,
    note: "",
    products: [
      {
        id: Date.now().toString(),
        name: "",
        quantity: 1,
        price: 0,
        allowOpening: true,
        allowFitting: true,
        allowExchange: true,
      },
    ],
    totalCost: 0,
    costs: {
      deliveryFees: 0,
      refusalFees: 0,
      cancellationFees: 0,
      deadline: "-",
    },
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: initialFormValues,
    mode: "onChange", // Enable real-time validation
    reValidateMode: "onChange", // Re-validate on every change
  });

  const handleAddCity = (city) => {
    form.setValue("customer.city", city.name);
    setCity(city.name);
  };

  const handleToggleCustomProduct = (value) => {
    form.setValue("fromStock", value);
    setSelectedProducts([]);

    if (value) {
      // Switching to inventory mode - clear products for multi-select
      form.setValue("products", []);
    } else {
      // Switching to custom mode - set single default product
      form.setValue("products", [
        {
          id: Date.now().toString(),
          name: "",
          quantity: 1,
          price: 0,
          allowOpening: true,
          allowFitting: true,
          allowExchange: true,
        },
      ]);
    }

    form.setValue("totalAmount", 0);
  };

  const handleAddProduct = (products) => {
    // MultiSelectProduct always returns an array
    const productArray = Array.isArray(products) ? products : [products];

    if (!form.getValues("fromStock")) {
      // For custom products, still use single selection
      const singleProduct = productArray[0];
      if (singleProduct) {
        form.setValue("products", [singleProduct]);
      }
      return;
    }

    // For inventory products, handle multi-selection with quantities
    const newProducts = productArray.map((product) => ({
      id: product._id,
      name: product.name,
      quantity: product.quantity || 1,
      price: product.price,
      allowOpening: product.allowOpening ?? true,
      allowFitting: product.allowFitting ?? true,
      allowExchange: product.allowExchange ?? true,
    }));

    setSelectedProducts(productArray);
    form.setValue("products", newProducts);
    updateTotalAmount(newProducts);
  };

  const handleTotalAmountNumber = (e) => {
    form.setValue("totalAmount", parseFloat(e.target.value));
  };

  const updateTotalAmount = (products) => {
    const total = products.reduce((sum, product) => {
      return sum + product.quantity * product.price;
    }, 0);
    form.setValue("totalAmount", total);
  };

  const handleSubmit = async (values) => {
    // If admin/manager is creating for a client, add the user field
    if ((auth.isAdmin || auth.isManager) && selectedClient) {
      values.user = selectedClient;
    }

    // Call parent onSubmit. Await its result so we can reset the form
    // after a successful create action. If onSubmit doesn't return a
    // promise, awaiting a non-promise is safe.
    try {
      const result = onSubmit(values);
      await result;

      if (action === "create") {
        // Reset form to initial values and clear local UI state
        form.reset(initialFormValues);
        setSelectedProducts([]);
        setSelectedClient(null);
        setSelectedClientObject(null);
        setCity(null);

        // Ensure totalAmount and costs reflect pricing query (if any)
        form.setValue("totalAmount", 0);
        form.setValue("totalCost", data?.deliveryFees);
        form.setValue("costs", {
          deliveryFees: data?.deliveryFees,
          refusalFees: data?.refusalFees,
          cancellationFees: data?.cancellationFees,
          deadline: data?.deadline,
        });
      }
    } catch {
      // Let parent handle errors; do not clear form on failure
      return;
    }
  };

  form.setValue("totalCost", data?.deliveryFees);
  form.setValue("costs", {
    deliveryFees: data?.deliveryFees,
    refusalFees: data?.refusalFees,
    cancellationFees: data?.cancellationFees,
    deadline: data?.deadline,
  });

  return (
    <Form {...form}>
      <PricingBar pricing={form.getValues("costs")} isLoading={isLoading} />
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Client Selector for Admin/Manager */}
        {(auth.isAdmin || auth.isManager) && (
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg">
                {t("users.selectClient")}
              </CardTitle>
              <CardDescription>
                {t("packages.form.clientSelectionHint")}
              </CardDescription>
            </CardHeader>
            <div className="px-6 pb-6">
              <SelectUser
                onSelectUser={(userId, user) => {
                  setSelectedClient(userId);
                  setSelectedClientObject(user);
                }}
                user={selectedClient}
                defaultUser={selectedClientObject}
                role="client"
                showDetails={true}
                clientSideSearch={true}
              />
            </div>
          </Card>
        )}

        {/* Customer Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="h-8 w-1 bg-primary rounded-full" />
              {t("packages.form.customerInfo")}
            </CardTitle>
          </CardHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="customer.name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("packages.form.customerName")}
                      <span className="text-red-500 ml-1">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("packages.form.customerName")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="customer.phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("packages.form.customerPhone")}
                      <span className="text-red-500 ml-1">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("users.phonePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormItem>
                <FormLabel>
                  {t("packages.form.customerCity")}
                  <span className="text-red-500 ml-1">*</span>
                </FormLabel>
                <SelectCity
                  city={form.getValues("customer.city")}
                  onSelectCity={handleAddCity}
                />
              </FormItem>
              <FormItem>
                <FormLabel>{t("packages.form.customerSecteur")}</FormLabel>
                <SelectQuartier
                  quartier={form.watch("customer.secteur")}
                  onSelectQuartier={(quartier) =>
                    form.setValue("customer.secteur", quartier)
                  }
                  cityName={city}
                />
                <FormMessage />
              </FormItem>
            </div>

            <FormField
              control={form.control}
              name="customer.address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("packages.form.customerAddress")}
                    <span className="text-red-500 ml-1">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("packages.form.customerAddress")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Card>

        {/* Products Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="h-8 w-1 bg-primary rounded-full" />
              {t("products.products")}
            </CardTitle>
          </CardHeader>
          <div className="px-6 pb-6 space-y-6">
            {/* Product Type Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectCard
                icon={<ShoppingBag className="h-5 w-5" />}
                title={t("products.custom")}
                isSelected={!form.getValues("fromStock")}
                onSelect={() => {
                  handleToggleCustomProduct(false);
                }}
              />
              <SelectCard
                icon={<Warehouse className="h-5 w-5" />}
                title={t("products.inventory")}
                isSelected={form.getValues("fromStock")}
                onSelect={() => {
                  handleToggleCustomProduct(true);
                }}
              />
            </div>

            {/* Inventory Product Selection */}
            {form.getValues("fromStock") && (
              <div className="space-y-4">
                <MultiSelectProduct
                  products={selectedProducts}
                  onSelect={handleAddProduct}
                  className="w-full"
                  placeholder={t("packages.form.selectProducts")}
                  showQuantity={true}
                  defaultQuantity={1}
                />
                {form.getValues("products")?.length === 0 && (
                  <div className="text-center p-8 border-2 border-dashed rounded-lg text-muted-foreground">
                    <Warehouse className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>{t("packages.form.noProductsSelected")}</p>
                  </div>
                )}
              </div>
            )}

            {/* Custom Product Form */}
            {!form.getValues("fromStock") &&
              form.getValues("products")?.map((product, index) => (
                <div
                  key={product.id}
                  className="space-y-4 p-4 border rounded-lg bg-muted/30"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name={`products.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("products.form.name")}
                            <span className="text-red-500 ml-1">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`products.${index}.price`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("products.form.price")}
                            <span className="text-red-500 ml-1">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              {...field}
                              onChange={(e) => {
                                field.onChange(parseFloat(e.target.value));
                                updateTotalAmount(form.getValues("products"));
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`products.${index}.quantity`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("packages.form.quantity")}
                            <span className="text-red-500 ml-1">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              {...field}
                              onChange={(e) => {
                                field.onChange(parseInt(e.target.value));
                                updateTotalAmount(form.getValues("products"));
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Product Options */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <FormField
                      control={form.control}
                      name={`products.${index}.allowOpening`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 bg-background">
                          <FormLabel className="text-sm font-normal">
                            {t("products.form.allowOpening")}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`products.${index}.allowFitting`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 bg-background">
                          <FormLabel className="text-sm font-normal">
                            {t("products.form.allowFitting")}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`products.${index}.allowExchange`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 bg-background">
                          <FormLabel className="text-sm font-normal">
                            {t("products.form.allowExchange")}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}

            {/* Total Amount */}
            <div className="pt-4 border-t">
              <FormField
                control={form.control}
                name="totalAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-semibold">
                      {t("packages.form.totalAmount")} ({t("common.currency")})
                      <span className="text-red-500 ml-1">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="text-lg font-semibold"
                        {...field}
                        onChange={handleTotalAmountNumber}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Card>

        {/* Additional Notes Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="h-8 w-1 bg-primary rounded-full" />
              {t("packages.form.note")}
            </CardTitle>
          </CardHeader>
          <div className="px-6 pb-6">
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder={t("packages.form.notePlaceholder")}
                      className="min-h-[100px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            disabled={actionIsLoading}
            size="lg"
            className="min-w-[200px]"
          >
            {t(`packages.form.submit.${action}`)}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PackageForm;
